import * as React from 'react';

import { Menu as MenuPrimitive } from '@base-ui/react/menu';

import { cn } from '@shared/lib/utils';

import { Separator } from './separator';

function DropdownMenu<Payload = unknown>({ ...props }: MenuPrimitive.Root.Props<Payload>) {
  return <MenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

const DropdownMenuTrigger = React.forwardRef<HTMLButtonElement, MenuPrimitive.Trigger.Props>(
  function DropdownMenuTrigger({ ...props }, ref) {
    return <MenuPrimitive.Trigger ref={ref} data-slot="dropdown-menu-trigger" {...props} />;
  }
);

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

function DropdownMenuContent({
  className,
  side = 'bottom',
  sideOffset = 8,
  align = 'end',
  alignOffset = 0,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className="outline-none"
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            'z-50 min-w-40 origin-(--transform-origin) overflow-hidden rounded-xl  bg-popover p-1 text-popover-foreground shadow-lg outline outline-border data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

const DropdownMenuGroup = React.forwardRef<HTMLDivElement, MenuPrimitive.Group.Props>(
  function DropdownMenuGroup({ className, ...props }, ref) {
    return (
      <MenuPrimitive.Group
        ref={ref}
        data-slot="dropdown-menu-group"
        className={cn('flex flex-col gap-0', className)}
        {...props}
      />
    );
  }
);

DropdownMenuGroup.displayName = 'DropdownMenuGroup';

const DropdownMenuItem = React.forwardRef<HTMLElement, MenuPrimitive.Item.Props>(
  function DropdownMenuItem({ className, ...props }, ref) {
    return (
      <MenuPrimitive.Item
        ref={ref}
        data-slot="dropdown-menu-item"
        className={cn(
          'flex cursor-default select-none items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-foreground outline-none transition-colors data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-muted [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4 [&_svg]:text-muted-foreground',
          className
        )}
        {...props}
      />
    );
  }
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="dropdown-menu-separator"
      orientation="horizontal"
      className={cn('my-1', className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
